/**
 * Google Analytics tracking utilities for consistent event tracking across the application
 * 
 * Event Categories:
 * - entity_actions: User interactions with entity action buttons (vote, save, share, comment)
 * - entity_specific: Entity-specific actions (participants, influences, sentiment, alignment)
 * - video_player: Video player interactions (play, pause, seek, volume, fullscreen, speed)
 * - user_engagement: General user engagement metrics
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

/**
 * Track a Google Analytics event with consistent structure
 * @param action - The action performed (e.g., 'click', 'play', 'vote')
 * @param category - The event category (e.g., 'entity_actions', 'video_player')
 * @param label - Optional label for additional context
 * @param value - Optional numeric value
 * @param customParameters - Additional custom parameters
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  customParameters?: Record<string, any>
): void => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      const eventData: Record<string, any> = {
        event_category: category,
        event_label: label,
        value: value,
        ...customParameters,
      };

      // Remove undefined values to keep the data clean
      Object.keys(eventData).forEach(key => {
        if (eventData[key] === undefined) {
          delete eventData[key];
        }
      });

      window.gtag("event", action, eventData);
    }
  } catch (error) {
    // Silently fail to avoid breaking the user experience
    console.warn("Analytics tracking failed:", error);
  }
};

/**
 * Entity Actions Tracking Functions
 */
export const trackEntityAction = {
  vote: (entityType: string, entityId: string | number, voteType?: "up" | "down") => {
    trackEvent("vote", "entity_actions", `${entityType}_vote`, undefined, {
      entity_type: entityType,
      entity_id: String(entityId),
      vote_type: voteType,
    });
  },

  comment: (entityType: string, entityId: string | number, commentCount?: number) => {
    trackEvent("comment_click", "entity_actions", `${entityType}_comment`, commentCount, {
      entity_type: entityType,
      entity_id: String(entityId),
      comment_count: commentCount,
    });
  },

  save: (entityType: string, entityId: string | number, action: "save" | "unsave") => {
    trackEvent("save", "entity_actions", `${entityType}_${action}`, undefined, {
      entity_type: entityType,
      entity_id: String(entityId),
      save_action: action,
    });
  },

  share: (entityType: string, entityId: string | number, shareMethod: string) => {
    trackEvent("share", "entity_actions", `${entityType}_share_${shareMethod}`, undefined, {
      entity_type: entityType,
      entity_id: String(entityId),
      share_method: shareMethod,
    });
  },
};

/**
 * Entity-Specific Actions Tracking Functions
 */
export const trackEntitySpecificAction = {
  viewParticipants: (actionId: string | number, participantCount?: number) => {
    trackEvent("view_participants", "entity_specific", "action_participants", participantCount, {
      action_id: String(actionId),
      participant_count: participantCount,
    });
  },

  viewInfluences: (issueId: string | number, influenceCount?: number) => {
    trackEvent("view_influences", "entity_specific", "issue_influences", influenceCount, {
      issue_id: String(issueId),
      influence_count: influenceCount,
    });
  },

  sentimentClick: (teabateId: string | number, sentimentType: "agree" | "disagree", previousSentiment?: string | null) => {
    trackEvent("sentiment_click", "entity_specific", `teabate_${sentimentType}`, undefined, {
      teabate_id: String(teabateId),
      sentiment_type: sentimentType,
      previous_sentiment: previousSentiment,
    });
  },

  sentimentRemove: (teabateId: string | number, sentimentType: "agree" | "disagree", method: "toast_dismiss" | "toast_timeout" | "immediate") => {
    trackEvent("sentiment_remove", "entity_specific", `teabate_remove_${sentimentType}`, undefined, {
      teabate_id: String(teabateId),
      sentiment_type: sentimentType,
      removal_method: method,
    });
  },

  alignmentModalOpen: (teabateId: string | number, stance: "agree" | "disagree", editMode: boolean) => {
    trackEvent("alignment_modal_open", "entity_specific", `teabate_alignment_${stance}`, undefined, {
      teabate_id: String(teabateId),
      stance: stance,
      edit_mode: editMode,
    });
  },

  argumentModalOpen: (teabateId: string | number, argumentType: "agree" | "disagree") => {
    trackEvent("argument_modal_open", "entity_specific", `teabate_argument_${argumentType}`, undefined, {
      teabate_id: String(teabateId),
      argument_type: argumentType,
    });
  },

  postCreate: (issueId: string | number) => {
    trackEvent("post_create_click", "entity_specific", "issue_post_create", undefined, {
      issue_id: String(issueId),
    });
  },
};

/**
 * Video Player Tracking Functions
 */
export const trackVideoPlayerAction = {
  play: (videoUrl: string, method: "click" | "auto" | "viewport") => {
    trackEvent("video_play", "video_player", method, undefined, {
      video_url: videoUrl,
      play_method: method,
    });
  },

  pause: (videoUrl: string, method: "click" | "auto" | "viewport", currentTime?: number) => {
    trackEvent("video_pause", "video_player", method, Math.round(currentTime || 0), {
      video_url: videoUrl,
      pause_method: method,
      current_time: Math.round(currentTime || 0),
    });
  },

  seek: (videoUrl: string, fromTime: number, toTime: number) => {
    trackEvent("video_seek", "video_player", "progress_bar", Math.round(toTime), {
      video_url: videoUrl,
      from_time: Math.round(fromTime),
      to_time: Math.round(toTime),
      seek_distance: Math.round(Math.abs(toTime - fromTime)),
    });
  },

  volumeToggle: (videoUrl: string, newState: "muted" | "unmuted") => {
    trackEvent("video_volume_toggle", "video_player", newState, undefined, {
      video_url: videoUrl,
      volume_state: newState,
    });
  },

  fullscreenToggle: (videoUrl: string, newState: "enter" | "exit") => {
    trackEvent("video_fullscreen", "video_player", newState, undefined, {
      video_url: videoUrl,
      fullscreen_state: newState,
    });
  },

  speedChange: (videoUrl: string, newSpeed: number, previousSpeed: number) => {
    trackEvent("video_speed_change", "video_player", `${previousSpeed}x_to_${newSpeed}x`, newSpeed, {
      video_url: videoUrl,
      new_speed: newSpeed,
      previous_speed: previousSpeed,
    });
  },

  error: (videoUrl: string, errorType?: string) => {
    trackEvent("video_error", "video_player", errorType || "unknown", undefined, {
      video_url: videoUrl,
      error_type: errorType,
    });
  },

  retry: (videoUrl: string) => {
    trackEvent("video_retry", "video_player", "retry_click", undefined, {
      video_url: videoUrl,
    });
  },

  ended: (videoUrl: string, duration?: number) => {
    trackEvent("video_ended", "video_player", "completed", Math.round(duration || 0), {
      video_url: videoUrl,
      duration: Math.round(duration || 0),
    });
  },
};

/**
 * Track user engagement metrics
 */
export const trackEngagement = {
  timeSpent: (page: string, timeInSeconds: number) => {
    trackEvent("time_spent", "user_engagement", page, timeInSeconds, {
      page: page,
      time_seconds: timeInSeconds,
    });
  },

  scrollDepth: (page: string, percentage: number) => {
    trackEvent("scroll_depth", "user_engagement", page, percentage, {
      page: page,
      scroll_percentage: percentage,
    });
  },
};

const analytics = {
  trackEvent,
  trackEntityAction,
  trackEntitySpecificAction,
  trackVideoPlayerAction,
  trackEngagement,
};

export default analytics;
